import { TEAM_SIZE, TICK_DT, TICK_HZ, type ClassId, type Faction } from '../core/constants.ts';
import type { Arena } from '../core/arena.ts';
import type { LevelGeometry } from '../core/level.ts';
import { makePlayer, type PlayerState } from '../core/player.ts';
import { hpAt, ppAt } from '../core/classes.ts';
import { defaultLoadout } from '../core/spells.ts';
import {
  emptyInput, makeSimState, step,
  type PlayerInput, type SimEvent, type SimState, type WorldQuery,
} from '../core/sim.ts';
import { despawnOwnedBy } from '../core/sim/placedEffects.ts';
import { ownerOf, sanitiseInput } from '../core/inputGuard.ts';
import { makeAwareness, observe, viewFor, type Awareness } from '../core/relevancy.ts';
import { canSpeak, makeChatLog, say, type ChatMessage, type ChatScope } from '../core/chat.ts';
import { BotDirector, type BOT_SKILLS } from '../core/ai/bot.ts';
import {
  carryInto, currentMap, endRound, joinLevel, makeSeries, rememberDeparture,
  seriesOver, seriesWinner, stillRemembered, type Departed,
} from '../core/series.ts';
import { BOT_CLASSES, BOT_NAMES, countdown, everyoneReady } from '../core/lobby.ts';
import { xpForLevel } from '../core/progression.ts';
import { makeMatch } from '../core/match.ts';
import { BODY_RADIUS, FACTIONS, WARMUP_SECONDS } from '../core/constants.ts';
import type { Vec3 } from '../core/math.ts';
import { arenaById } from '../core/arenas/index.ts';

/**
 * How long the scoreboard sits between rounds, in seconds.
 *
 * Long enough to read a result and see the set score move, short enough that
 * nobody goes to make tea. Ten is about where round-based shooters have settled
 * and there is no reason to be original about it.
 */
const INTERMISSION_SECONDS = 10;
import { buildNavGrid } from '../core/ai/nav.ts';

/**
 * One match, and everything the server is authoritative over.
 *
 * The half of `LocalTransport` that was never really a transport. That class
 * owned the simulation *and* pretended to be a network, which was fine while
 * there was one player and impossible to extend to two: there was nowhere to put
 * a second client, because the thing holding the state also assumed it was the
 * thing being played.
 *
 * A room knows nothing about who is nearby or what is being drawn. It takes
 * inputs keyed by connection, advances at a fixed rate, and hands each member a
 * view of the world *they* are entitled to. Whether the connection is a socket
 * or a function call in the same process is not its business — which is the
 * whole point, because it makes the loopback and the network the same code with
 * a different pipe.
 */

/**
 * How long an empty room is kept before it is reaped, in seconds.
 *
 * Long enough to cover opening a room and sitting down in it — two messages with
 * a network round trip between them — and short enough that a server does not
 * fill with rooms whose hosts changed their minds. Thirty seconds also covers
 * the more annoying case: the last player disconnecting a moment before a friend
 * arrives, which without a window at all is a room that vanishes between one
 * person leaving and the next one knocking.
 */
export const ABANDON_GRACE = 30;

/**
 * How many ticks an input is held for after it stops arriving.
 *
 * Half a second at sixty hertz. Long enough that no gap a running client can
 * produce reaches it, short enough that a client which has actually gone quiet
 * stops rather than walking into a wall for ever.
 */
const INPUT_HOLD_TICKS = 30;
/**
 * How many sequenced inputs a connection may have waiting. Six ticks is a tenth
 * of a second of slack for network jitter; past it the oldest are dropped,
 * because a queue that only grows is latency that only grows.
 */
const INPUT_QUEUE_MAX = 6;
/**
 * A standing backlog is latency that never goes away: a burst of jitter queues
 * three inputs, and from then on every input waits three ticks. More than this
 * many waiting for half a second, and the room skips one to catch up — a single
 * tick's correction on the client, once, instead of 50 ms of lag for ever.
 */
const INPUT_BACKLOG = 2;
const INPUT_BACKLOG_TICKS = 30;

export interface RoomSettings {
  /** What is being played, and where. */
  mode: string;
  arena: Arena;
  geometry: LevelGeometry;

  /** How long a round runs, in seconds. */
  roundSeconds?: number;
  /** Multiplier on all experience earned. A persistent server's dial. */
  xpScale?: number;
  /** How many may be in here at once, across all sides. */
  capacity?: number;
  /** Rounds in the set: 1, 3 or 5. One means a single match and no series. */
  rounds?: number;
  /** Maps in rotation, in order. A round is played on the next one along. */
  mapPool?: string[];
  /** Whether levels earned in a round survive into the next. */
  carryXp?: boolean;
  /** The pre-round freeze. Zero starts a match the instant it is advanced. */
  warmupSeconds?: number;
  /**
   * How long the room waits in its lobby before starting itself.
   *
   * Zero means no lobby: the match is live as soon as it is advanced, which is
   * what a test and a practice room want. A hosted room passes `LOBBY_SECONDS`.
   */
  lobbySeconds?: number;
  /**
   * Whether the room survives being empty.
   *
   * Ephemeral rooms are made for a match and die with it. A persistent one is a
   * *server* in the older sense — it has a name, a set of rules and a map
   * rotation, and it is there tomorrow. Both are the same object; only this
   * decides whether anything reaps it.
   */
  persistent?: boolean;
  difficulty?: keyof typeof BOT_SKILLS;
}

export interface Member {
  id: string;
  name: string;
  /** True when nothing is driving this one from outside. */
  bot: boolean;
}

/** What a client is handed each time it asks. */
export interface RoomView {
  state: SimState;
  events: SimEvent[];
  chat: ChatMessage[];
}

export class GameRoom {
  readonly id: string;
  readonly settings: RoomSettings;

  private readonly state: SimState;
  private readonly world: WorldQuery;
  private readonly director: BotDirector;
  private readonly chatLog = makeChatLog();

  /** Inputs arriving between ticks, one per connection. Last one wins. */
  private readonly pending = new Map<string, PlayerInput>();
  /**
   * The tick each player's last input arrived on.
   *
   * Kept so a held input can be let go of. See `advance`.
   */
  private readonly inputAt = new Map<string, number>();
  /** What each member has been allowed to notice, for relevancy. */
  private readonly awareness = new Map<string, Awareness>();
  /**
   * Events and chat each member has not yet collected.
   *
   * Per member rather than shared, because two clients ask at different moments
   * and a single queue would hand the first one everything and the second one
   * nothing. On a socket this becomes what is written down the pipe.
   */
  private readonly outbox = new Map<string, { events: SimEvent[]; chat: ChatMessage[] }>();

  private readonly bots = new Set<string>();
  /** The newest input seen from each client, and the newest actually stepped. */
  private readonly seen = new Map<string, number>();
  private readonly acked = new Map<string, number>();
  /**
   * Sequenced inputs not yet simulated, oldest first, per connection.
   *
   * One is consumed per tick, and the acknowledgement names the one consumed.
   * Before this, a newer input simply replaced an older one and the room acked
   * the newest it had *received* — so whenever two arrived inside one tick, the
   * room simulated one and told the client it had simulated both, and whenever
   * none arrived it repeated the last. Client and room tick on different clocks,
   * so that happened constantly, and every snapshot pulled the predicted mage a
   * tick forward or back: the hosted game felt heavy and choppy at any frame
   * rate, with no single cause anybody could point at.
   */
  private readonly queued = new Map<string, { input: PlayerInput; seq: number }[]>();
  /** Consecutive ticks each connection has had more than `INPUT_BACKLOG` waiting. */
  private readonly backlogged = new Map<string, number>();
  /** Dummies that shoot back. See `addDummy`. */
  private readonly hostile = new Set<string>();
  /** Mages whose connection went away, and what they had when it did. */
  private readonly departed = new Map<string, Departed>();
  private accumulator = 0;
  /** How long the lobby has been open, and how long it has been ready. */
  private waited = 0;
  private readyFor = 0;
  /** Set once somebody has taken the set, so the last scoreboard is the last. */
  private seriesDone = false;

  constructor(id: string, settings: RoomSettings, world: WorldQuery) {
    this.id = id;
    this.settings = settings;
    this.world = world;
    /*
     * The lobby's rules go in at construction, because both of them shape the
     * state rather than sitting beside it: the clock starts at the round length
     * and the experience rate has to be in every snapshot so a predicting client
     * levels up at the same instant the room does.
     */
    /*
     * A set rather than a single round, when a lobby asked for one.
     *
     * `bestOf` was a number on a form for a long time: `series.ts` had the whole
     * model — rounds won, rotation, what a mage keeps — and nothing imported it,
     * so a best-of-five played one round and stopped at the scoreboard. This is
     * where it lands.
     *
     * A one-round lobby gets no series at all rather than a series of one. The
     * distinction matters downstream: a HUD showing "round 1 of 1" and a set
     * score of nought all round is noise, and a single match should look like a
     * single match.
     */
    const rounds = settings.rounds ?? 1;
    const series = rounds > 1
      ? makeSeries(rounds, settings.mapPool ?? [], settings.carryXp ?? true)
      : null;

    this.state = makeSimState(settings.arena, [], undefined, {
      roundSeconds: settings.roundSeconds,
      xpScale: settings.xpScale,
      series,
      warmupSeconds: settings.warmupSeconds ?? WARMUP_SECONDS,
      lobbySeconds: settings.lobbySeconds ?? 0,
    });
    /*
     * A room with a lobby starts in it.
     *
     * Opt-in, like the freeze, and for the same reason: a bare state and every
     * core test wants a match that plays the moment it is stepped. A hosted room
     * asks for one because the lobby is where people gather, and it is the room
     * that has to say who is there — a client cannot, which is what made two
     * people in the same lobby invisible to each other.
     */
    if ((settings.lobbySeconds ?? 0) > 0) this.state.match.phase = 'lobby';
    this.director = new BotDirector(
      buildNavGrid(settings.geometry),
      world,
      settings.difficulty ?? 'normal',
    );
  }

  get members(): Member[] {
    return this.state.players.map((p) => ({
      id: p.id, name: p.name, bot: this.bots.has(p.id),
    }));
  }

  /**
   * A spawn for the next mage on a side, spread out from the ones already there.
   *
   * The old index was `players.length % spawns.length` — the *total* head count,
   * not the count on that side. With three spawns per faction and three sides,
   * every Order mage got the same one: nine players meant indices 0, 3 and 6,
   * which are all zero modulo three. Everybody on a side started inside
   * everybody else on it.
   *
   * It went unnoticed because the simulation started immediately and the pile
   * resolved itself within a second, as the character controllers pushed apart.
   * The pre-round freeze holds it still for ten seconds instead, so what used to
   * be a frame of overlap is now a long look at the inside of a teammate's head.
   *
   * Past the end of the list, mages ring the spawn they would have shared. A
   * ring rather than a line so no direction is privileged, and at a radius clear
   * of two body widths so nobody starts inside anybody. Authored maps should
   * carry enough spawns that this never runs; it is here so that a map with
   * three does not stack eight people on them.
   */
  private spawnFor(side: Faction, ordinal = this.countOn(side)): Vec3 {
    const spawns = this.settings.arena.spawns[side];
    const base = spawns[ordinal % spawns.length]!;
    const ring = Math.floor(ordinal / spawns.length);
    if (ring === 0) return { ...base };

    // Offset by a whole turn divided among however many share this spawn, turned
    // a little each ring so two rings do not line up radially.
    const around = (ordinal % spawns.length) + ring * 2.399;
    // Four body radii per ring: two mages on the same ring are a clear body
    // width apart, and the first ring stands well outside the mage on the spawn
    // rather than brushing them.
    const radius = BODY_RADIUS * 4 * ring;
    return {
      x: base.x + Math.cos(around) * radius,
      y: base.y,
      z: base.z + Math.sin(around) * radius,
    };
  }

  /**
   * A bot from whichever side has the most, or null if there are none.
   *
   * Taken from the largest side so that making room for a person cannot leave
   * the sides less even than it found them.
   */
  private largestSideBot(): string | null {
    const counts = { order: 0, balance: 0, chaos: 0 } as Record<Faction, number>;
    for (const p of this.state.players) counts[p.faction] += 1;

    let best: string | null = null;
    let bestCount = -1;
    for (const p of this.state.players) {
      if (!this.bots.has(p.id)) continue;
      if (counts[p.faction] > bestCount) {
        bestCount = counts[p.faction];
        best = p.id;
      }
    }
    return best;
  }

  /** How many are already on a side, which is which spawn the next one takes. */
  private countOn(side: Faction): number {
    return this.state.players.reduce((n, p) => (p.faction === side ? n + 1 : n), 0);
  }

  /**
   * The side a newcomer should be on: the one with fewest, unless they asked for
   * a side that is already tied for fewest.
   *
   * Bots are counted. A side propped up by three bots is not short-handed, and
   * filling it with people while the bots stay would put five against two.
   */
  private balancedFaction(wanted: Faction): Faction {
    /*
     * Only once the match is underway. Before that, their choice is theirs.
     *
     * The first version balanced every join and broke the case it was written to
     * protect: two friends opening a lobby and both picking Order got split,
     * because after the first of them Order was no longer tied for smallest. A
     * room nobody has played in yet has no balance to preserve — it has three
     * empty sides and some people deciding who they want to play with.
     *
     * `tick` is the test because a room only ticks while it is advancing, so
     * zero means the match has not started, and it stays truthful through a
     * scoreboard and a pre-round freeze — which is when people actually arrive,
     * since that is when a slot opens.
     */
    if (this.state.tick === 0) return wanted;

    const counts = { order: 0, balance: 0, chaos: 0 } as Record<Faction, number>;
    for (const p of this.state.players) counts[p.faction] += 1;

    const fewest = Math.min(...FACTIONS.map((f) => counts[f]));
    // Their choice stands whenever it is already one of the smallest — which is
    // every join into an even match, and so most of them.
    if (counts[wanted] === fewest) return wanted;
    return FACTIONS.find((f) => counts[f] === fewest) ?? wanted;
  }

  /** How full this room is, which is what a lobby list wants to show. */
  get population(): { players: number; capacity: number } {
    return {
      players: this.state.players.length,
      capacity: this.settings.capacity ?? 24,
    };
  }

  /**
   * Adds somebody. `connectionId` is the identity the server trusts.
   *
   * Returned rather than taken, so a client cannot choose to be somebody who is
   * already here — the id belongs to the connection and nothing a client sends
   * can change it. See `ownerOf`.
   */
  join(
    connectionId: string,
    name: string,
    faction: Faction,
    cls: ClassId,
    loadout?: string[],
    /** Set for a seat somebody chose deliberately, which balancing must not move. */
    exact = false,
  ): PlayerState | null {
    if (this.state.players.some((p) => p.id === connectionId)) return null;

    /*
     * A person may always take a bot's seat.
     *
     * Bots exist to make a half-empty room playable, so a room they have filled
     * turning a person away is exactly backwards — and it is not hypothetical:
     * a lobby opened with four bots a side fills twelve of twelve seats and then
     * refuses the host who asked for them. The bot taken is from the largest
     * side, so seating somebody never makes the balance worse.
     */
    const { players, capacity } = this.population;
    if (players >= capacity) {
      const evict = this.largestSideBot();
      if (!evict) return null;
      this.removeBot(evict);
    }

    const now = this.state.tick * TICK_DT;

    /*
     * Somebody coming back, if they were only briefly gone.
     *
     * This is for dropped connections, which are common and were being punished
     * absurdly: a player whose wifi blinked at minute eight came back as a fresh
     * drop-in and lost everything they had earned. Their side and their
     * discipline come back with the levels, because as far as the room is
     * concerned they never really left — which also closes the hole where
     * leaving and rejoining was a way to change class mid-match.
     */
    const known = this.departed.get(connectionId);
    const returning = known && stillRemembered(known, now) ? known : null;
    if (returning) this.departed.delete(connectionId);

    /*
     * A returning player gets their old side back; a new one gets the short-
     * handed one.
     *
     * Sides do not stay even on their own. People leave from whichever side is
     * losing, and a set that runs five rounds ends up four against one unless
     * something pushes back — so somebody arriving mid-match is put where they
     * are needed rather than where they clicked. The request is still honoured
     * whenever it does not make things worse, which covers the case everybody
     * actually cares about: joining with a friend at the start.
     */
    const side = returning ? returning.faction
      : exact ? faction
        : this.balancedFaction(faction);
    const spawn = this.spawnFor(side);
    const player = makePlayer(
      connectionId, name, side, (returning?.cls as ClassId) ?? cls, { ...spawn },
    );

    if (returning) {
      player.xp = returning.xp;
      player.level = returning.level;
    } else {
      /*
       * Arriving late, at the bottom of the field rather than at the bottom.
       *
       * With experience carrying across rounds a best-of-five can be on its
       * fifth with everybody at twenty, and a level one dropped into that is not
       * playing the same game as the people shooting at them — they are a source
       * of experience for whoever finds them first.
       *
       * The floor is the *lowest* level in play, not the average: matching the
       * middle would hand a newcomer a head start over somebody who has been
       * here all set, which is its own unfairness pointing the other way. And it
       * is the *start* of that level, so the person one kill from the next one
       * keeps that.
       *
       * Unconditional now. It used to run only while the phase was `live`, so
       * anybody arriving during a scoreboard or a pre-round freeze — which is
       * exactly when people join, because that is when a slot opens — came in at
       * level one regardless. `joinLevel` returns 1 when nobody is in play, so a
       * fresh match is unaffected and the special case bought nothing.
       */
      player.level = joinLevel(this.state.players);
      player.xp = xpForLevel(player.level);
    }

    if (loadout && !returning) player.loadout = [...loadout];

    this.state.players.push(player);
    this.awareness.set(connectionId, makeAwareness(connectionId));
    this.outbox.set(connectionId, { events: [], chat: [] });
    return player;
  }

  /** Adds one the room drives itself. */
  /**
   * Takes a bot out, to make room for a person.
   *
   * `leave` does the work — it is the same departure either way — and this only
   * exists so the bot set is tidied alongside. A bot is not remembered the way a
   * dropped player is, which `leave` already knows.
   */
  private removeBot(id: string): void {
    this.leave(id);
    this.bots.delete(id);
  }

  /** Whether a seat is held by a bot, which a lobby list shows and a reaper asks. */
  isBot(id: string): boolean {
    return this.bots.has(id);
  }

  addBot(name: string, faction: Faction, cls: ClassId): string {
    const id = `bot_${this.state.players.length}_${faction}`;
    /*
     * Placed, not balanced.
     *
     * `join` moves a newcomer to the short-handed side once a match is under
     * way, which is right for a person wandering in and wrong for a bot: a bot
     * *is* the balancing, and somebody pressing the button on the Chaos column
     * has already said which side needs one. The room ticks during its lobby, so
     * the "match is under way" test was true and every bot went wherever the
     * count said rather than where it was asked for.
     */
    if (!this.join(id, name, faction, cls, undefined, true)) return '';
    this.bots.add(id);
    return id;
  }

  /**
   * A practice target: a mage that stands still and, optionally, shoots back.
   *
   * Not a bot. A bot has doctrine, a route and an opinion about pools, and none
   * of that is wanted when the question is "how much damage does Fire Bolt do at
   * this range". A dummy is the smallest opponent that makes dying possible.
   */
  addDummy(
    name: string, faction: Faction, cls: ClassId,
    pos: { x: number; y: number; z: number },
    opts: { attacks?: boolean; level?: number } = {},
  ): string {
    const id = `dummy_${this.state.players.length}`;
    const dummy = makePlayer(id, name, faction, cls, pos);

    if (opts.level && opts.level > 1) {
      dummy.level = opts.level;
      dummy.hpMax = hpAt(cls, opts.level);
      dummy.ppMax = ppAt(cls, opts.level);
      dummy.hp = dummy.hpMax;
      dummy.pp = dummy.ppMax;
    }
    // Frozen so an attacking dummy cannot out-level the player shooting at it.
    dummy.frozenLevel = true;

    this.state.players.push(dummy);
    this.awareness.set(id, makeAwareness(id));
    this.outbox.set(id, { events: [], chat: [] });
    if (opts.attacks) this.hostile.add(id);
    return id;
  }

  /**
   * The simplest possible opponent: face the nearest living enemy and fire when
   * the shot is clear. No movement, no evasion — just enough pressure to make
   * dying possible.
   */
  private dummyInputs(): Map<string, PlayerInput> {
    const out = new Map<string, PlayerInput>();

    for (const id of this.hostile) {
      const self = this.state.players.find((p) => p.id === id);
      if (!self || !self.alive || self.ghost) continue;

      const eye = { x: self.pos.x, y: self.pos.y + 1.55, z: self.pos.z };
      const candidates = this.state.players
        .filter((o) => o.faction !== self.faction && o.alive && !o.ghost)
        .map((o) => ({ o, d: Math.hypot(o.pos.x - self.pos.x, o.pos.z - self.pos.z) }))
        .filter((c) => c.d <= DUMMY_RANGE)
        .sort((a, b) => a.d - b.d);

      // Nearest first, falling through to the next when the shot is blocked.
      for (const { o: target } of candidates) {
        const aim = { x: target.pos.x, y: target.pos.y + 1.0, z: target.pos.z };
        if (this.world.segmentBlocked(eye, aim)) continue;

        const dx = aim.x - eye.x;
        const dy = aim.y - eye.y;
        const dz = aim.z - eye.z;
        const len = Math.hypot(dx, dy, dz) || 1;

        const input = emptyInput();
        input.pitch = Math.asin(dy / len);
        input.yaw = Math.atan2(-dx / len, -dz / len);
        input.fire = true;
        out.set(id, input);
        break;
      }
    }
    return out;
  }

  leave(connectionId: string): void {
    const at = this.state.players.findIndex((p) => p.id === connectionId);
    if (at >= 0) {
      // Held for a couple of minutes, in case the connection comes back.
      if (!this.bots.has(connectionId)) {
        this.departed.set(
          connectionId,
          rememberDeparture(this.state.players[at]!, this.state.tick * TICK_DT),
        );
      }
      this.state.players.splice(at, 1);
      /*
       * Whatever they left standing goes with them. A wall, sign or storm with
       * nobody behind it has no side to answer to, no kill to credit and no
       * owner to come back for it — and Wall of Cold, judging friendly fire by
       * its owner, briefly hurt the team that raised it.
       */
      despawnOwnedBy(this.state, connectionId);
    }
    this.awareness.delete(connectionId);
    this.outbox.delete(connectionId);
    this.pending.delete(connectionId);
    this.inputAt.delete(connectionId);
    this.seen.delete(connectionId);
    this.queued.delete(connectionId);
    this.backlogged.delete(connectionId);
    this.acked.delete(connectionId);
  }

  /**
   * Takes an input from a connection.
   *
   * Two things happen here that must never be skipped: the input is cleaned of
   * anything the game cannot mean, and it is filed under the *connection's* id
   * rather than whatever the message claimed. The second is the difference
   * between a multiplayer game and one where anybody can drive anybody.
   */
  input(connectionId: string, raw: unknown, claimedId?: string, seq?: number): void {
    const owner = ownerOf(connectionId, claimedId);
    if (!this.state.players.some((p) => p.id === owner)) return;
    const input = sanitiseInput(raw);

    /*
     * Sequenced: queued, and acknowledged only once simulated (see `queued`).
     * Unsequenced — a client in this same process — is the old arrangement: the
     * latest input stands until replaced, which is right when there is one input
     * per frame and nothing in between to lose.
     */
    if (typeof seq === 'number' && Number.isFinite(seq)) {
      let queue = this.queued.get(owner);
      if (!queue) this.queued.set(owner, (queue = []));
      const last = queue.length ? queue[queue.length - 1]!.seq : this.seen.get(owner) ?? -1;
      // Far behind is not a straggler but a client that started counting again —
      // a fresh prediction after a reconnect. Start again with it.
      if (seq <= last - 600) {
        queue.length = 0;
        this.seen.delete(owner);
      } else if (seq <= last) {
        return; // a duplicate or a straggler: already handled
      }
      queue.push({ input, seq });
      while (queue.length > INPUT_QUEUE_MAX) queue.shift();
      return;
    }
    this.pending.set(owner, input);
    this.inputAt.set(owner, this.state.tick);

    /*
     * Noted, not acted on.
     *
     * The room does nothing with a sequence number except hand it back, which is
     * exactly right: it is the *client's* bookkeeping, and the room's only job is
     * to say how far through it has got so the client knows what still needs
     * replaying.
     */
  }

  /**
   * One queued input per connection into the tick about to run.
   *
   * A connection with nothing queued keeps its last input (see the hold in
   * `advance`) and its acknowledgement does not move — the room has simulated
   * a tick the client did not ask for, and saying so is what lets the client's
   * next replay put it right.
   */
  private takeQueued(): void {
    for (const [id, queue] of this.queued) {
      const behind = queue.length > INPUT_BACKLOG ? (this.backlogged.get(id) ?? 0) + 1 : 0;
      this.backlogged.set(id, behind);
      if (behind > INPUT_BACKLOG_TICKS) {
        // Skipped, and acknowledged as such: the client stops replaying it.
        const skipped = queue.shift()!;
        this.seen.set(id, skipped.seq);
        this.backlogged.set(id, 0);
      }
      const next = queue.shift();
      if (!next) continue;
      this.pending.set(id, next.input);
      this.inputAt.set(id, this.state.tick);
      this.seen.set(id, next.seq);
    }
  }

  /** The last input this room processed from a given connection. */
  ackFor(connectionId: string): number {
    return this.acked.get(connectionId) ?? -1;
  }

  /** Takes a line of chat, if the speaker is allowed to say it. */
  speak(connectionId: string, scope: ChatScope, text: unknown): ChatMessage | null {
    const speaker = this.state.players.find((p) => p.id === connectionId);
    if (!speaker) return null;

    const msg = say(
      this.chatLog, speaker, scope, text,
      this.state.match.phase, this.state.tick * TICK_DT,
    );
    if (!msg) return null;

    // Delivered to whoever may hear it, filtered on receipt rather than on send.
    for (const [id, box] of this.outbox) {
      const listener = this.state.players.find((p) => p.id === id);
      if (listener && (msg.scope === 'all' || listener.faction === msg.faction)) {
        box.chat.push(msg);
      }
    }
    return msg;
  }

  /**
   * Whether somebody may change discipline right now.
   *
   * Only between rounds. A round reads by what everybody brought to it, and a
   * Magician who becomes a Cleric halfway through is a mage their enemies have
   * already decided how to fight. Between rounds it is the opposite — it is the
   * counterplay a losing side has, and the way somebody finds out what the other
   * classes feel like.
   */
  get mayChangeClass(): boolean {
    return this.state.match.phase !== 'live';
  }

  /** Whether general chat is open right now, for a client to grey out its key. */
  get generalChatOpen(): boolean {
    return canSpeak('all', this.state.match.phase);
  }

  /**
   * Advances the match by real time, in fixed steps.
   *
   * The accumulator is the room's rather than a client's, because there is one
   * simulation and it must not run at the rate of whoever happens to be looking
   * at it. Bots are asked for input here too — a bot is a member with nothing on
   * the other end of its connection, and nothing else about it is different.
   */
  advance(elapsed: number): void {
    this.accumulator += Math.min(0.25, elapsed);

    let steps = 0;
    while (this.accumulator >= TICK_DT && steps < TICK_HZ) {
      this.takeQueued();
      const inputs = new Map<string, PlayerInput>(this.pending);
      if (this.hostile.size) {
        for (const [id, input] of this.dummyInputs()) inputs.set(id, input);
      }
      if (this.bots.size) {
        for (const [id, input] of this.director.inputs(this.state, [...this.bots], TICK_DT)) {
          inputs.set(id, input);
        }
      }

      // Where everybody was, so the renderer can draw between two ticks.
      for (const p of this.state.players) p.prevPos = { ...p.pos };

      // Every mage's capsule where the simulation last left it, before anybody
      // moves — so the first to move this tick bumps into everybody else.
      this.world.syncBodies?.(this.state.players);
      const events = step(this.state, inputs, this.world, TICK_DT);
      /*
       * Solid spell walls into the world's collision, before the next tick
       * moves anybody. A wall raised this tick is solid on the next — on the
       * server as on the client, which mirrors the same list every frame.
       */
      this.world.syncBarriers?.(this.state.barriers);
      /*
       * Before the outbox, not after.
       *
       * `push(...events)` copies what is in the array *at that moment*, so a
       * round-over written afterwards went into the local list and reached
       * nobody — the room advanced its set correctly and no client was ever told
       * a round had ended. Found by a test asserting the announcement rather
       * than the state, which is the reason to assert both.
       */
      this.tickLobby(TICK_DT, events);
      this.tickSeries(TICK_DT, events);
      for (const box of this.outbox.values()) box.events.push(...events);

      // Whatever had arrived by this tick has now been simulated, so that is
      // what each client is told it may stop replaying.
      for (const [id, seq] of this.seen) this.acked.set(id, seq);

      this.accumulator -= TICK_DT;
      steps += 1;
    }

    /*
     * Inputs are consumed once per call, not once per tick.
     *
     * The rule this implements has not changed: a client sends one input per
     * frame, the room may tick at a different rate, and holding an input for
     * ever would mean a client that stopped sending kept walking. So it is
     * cleared, and a client that goes quiet stops.
     *
     * Clearing it *inside* the loop was the bug, and it only showed up below
     * sixty frames a second. A slow frame produces two ticks; the first took the
     * input and emptied the map, and the second ran with nothing in it — so a
     * player holding forward moved on one tick out of two and everybody in the
     * match was slower than they should be in proportion to how hard their own
     * machine was working. Which is a frame rate problem that had become a
     * balance problem: two people in the same fight moved at different speeds
     * because one of them was looking at a busier room.
     *
     * The input belongs to the frame it was sent for, and every tick that frame
     * produces is part of that frame. Moved out of the loop, it is.
     */
    /*
     * Inputs are *held*, not consumed — for a short while.
     *
     * Clearing after every call was right when the only client was in the same
     * process, driving `advance` with its own frame time: one input per call,
     * one call per frame, nothing ever missing. Over a socket the two rates come
     * apart. The room ticks sixty times a second whatever happens, and a client
     * sends at its frame rate — so a client below sixty, or one that simply
     * drops a frame, leaves ticks with nothing in the map at all. Core reads
     * that as a player who let go of the keys: a mage moving at frame-rate over
     * sixty of their proper speed, and a visible hitch on every dropped frame.
     *
     * Which is the same bug as the local one from before, arriving by a
     * different road, and it has the same shape of answer: the last thing
     * somebody asked for is still what they are asking for until they say
     * otherwise or stop talking.
     *
     * The timeout is what stops a held input becoming a mage who walks for ever
     * because a tab was closed. Half a second is long enough to cover any gap a
     * running client produces — at ten frames a second a gap is a tenth of that
     * — and short enough that a dropped connection stops before anybody watching
     * would call it a ghost.
     */
    const stale = this.state.tick - INPUT_HOLD_TICKS;
    for (const [id, at] of this.inputAt) {
      if (at >= stale) continue;
      this.pending.delete(id);
      this.inputAt.delete(id);
    }
  }

  /** How far into the unsimulated tick we are, for drawing between two. */
  get alpha(): number {
    return Math.min(1, this.accumulator / TICK_DT);
  }

  /**
   * What one member is entitled to see, and everything waiting for them.
   *
   * Collecting empties the outbox, which is what makes this safe to call from
   * two clients at different rates — each gets what happened since *they* last
   * asked rather than since anybody did.
   */
  collect(connectionId: string, filtered = true): RoomView {
    const box = this.outbox.get(connectionId);
    const events = box ? box.events.splice(0) : [];
    const chat = box ? box.chat.splice(0) : [];

    const know = this.awareness.get(connectionId);
    if (!filtered || !know) return { state: this.state, events, chat };

    const now = this.state.tick * TICK_DT;
    observe(this.state, know, this.world, now);
    return { state: viewFor(this.state, know, now), events, chat };
  }

  /** The unfiltered truth. For the room's own use and for tests, never a client. */
  get authoritative(): SimState {
    return this.state;
  }

  /**
   * Says whether somebody is ready, which is the only thing a lobby is deciding.
   *
   * Ignored outside the lobby, so a stray click during a round cannot do
   * anything. A player who is not seated is not answered at all.
   */
  setReady(connectionId: string, on: boolean): boolean {
    if (this.state.match.phase !== 'lobby') return false;
    // The connection id is the seat id; a room keys players by it directly.
    const p = this.state.players.find((x) => x.id === connectionId);
    if (!p) return false;
    p.ready = on;
    return true;
  }

  /**
   * Adds a bot to a side, if the lobby is open and there is room.
   *
   * Capped per side the way the muster was: a side holds `TEAM_SIZE`, and a bot
   * may not take the last seat on a side somebody is standing on.
   */
  addBotTo(faction: Faction): string {
    if (this.state.match.phase !== 'lobby') return '';
    const onSide = this.state.players.filter((p) => p.faction === faction).length;
    if (onSide >= TEAM_SIZE) return '';
    if (this.population.players >= this.population.capacity) return '';
    return this.addBot(
      BOT_NAMES[this.bots.size % BOT_NAMES.length]!,
      faction,
      BOT_CLASSES[this.bots.size % BOT_CLASSES.length]!,
    );
  }

  /**
   * Changes what somebody is called, in the lobby only.
   *
   * The lobby is the only place a name is asked for, and the room is joined
   * before that screen opens — so a name typed there reached a local variable
   * and nothing else, and everybody went on seeing whatever the client happened
   * to connect as.
   */
  rename(connectionId: string, name: unknown): boolean {
    if (this.state.match.phase !== 'lobby') return false;
    const p = this.state.players.find((x) => x.id === connectionId);
    if (!p) return false;
    // Trimmed and bounded here rather than trusted: a name is drawn above a
    // mage's head and printed in the log, and both have a width.
    const clean = String(name ?? '').replace(/\s+/g, ' ').trim().slice(0, 20);
    if (!clean) return false;
    p.name = clean;
    return true;
  }

  /**
   * Changes somebody's side or discipline, in the lobby only.
   *
   * A lobby that cannot do this is a list rather than a lobby: the first thing
   * anybody does on seeing three uneven columns is move to the short one. Once a
   * round is running it is refused — swapping sides mid-fight is a different
   * feature with different rules, and this is not it.
   */
  reseat(
    connectionId: string, faction: Faction, cls: ClassId, loadout?: string[],
  ): boolean {
    if (this.state.match.phase !== 'lobby') return false;
    const p = this.state.players.find((x) => x.id === connectionId);
    if (!p) return false;

    if (p.faction !== faction) {
      p.faction = faction;
      const spawn = this.spawnFor(faction);
      p.pos = { ...spawn };
      p.prevPos = { ...spawn };
      this.world.place?.(p);
    }
    if (p.cls !== cls) {
      p.cls = cls;
      p.hpMax = hpAt(cls, p.level);
      p.ppMax = ppAt(cls, p.level);
      p.hp = p.hpMax;
      p.pp = p.ppMax;
      p.loadout = defaultLoadout(cls);
      p.cooldowns = p.loadout.map(() => 0);
      p.selectedSlot = 0;
    }
    if (loadout?.length) {
      p.loadout = [...loadout];
      p.cooldowns = p.loadout.map(() => 0);
    }
    return true;
  }

  /**
   * Runs the lobby clock, and starts the match when it is done.
   *
   * The rules are `core/lobby.ts`'s and always were — `countdown` and
   * `everyoneReady` were written, tested and then never called by anything,
   * because the only lobby that existed was a screen each client drew for
   * itself. This is where they finally live: one clock, in the room, that every
   * client is shown.
   */
  private tickLobby(dt: number, out: SimEvent[]): void {
    const s = this.state;
    if (s.match.phase !== 'lobby') return;

    this.waited += dt;
    const members = s.players.map((p) => ({
      id: p.id,
      name: p.name,
      faction: p.faction,
      cls: p.cls,
      level: p.level,
      ready: this.bots.has(p.id) ? true : p.ready,
      bot: this.bots.has(p.id),
      joinedAt: 0,
    }));

    // How long everybody has been ready, which is what turns the hurry into a
    // deadline rather than a ceiling that resets every time it is looked at.
    if (everyoneReady(members)) this.readyFor += dt;
    else this.readyFor = 0;

    s.lobbyLeft = countdown(this.waited, members, this.readyFor);
    if (s.lobbyLeft > 0) return;

    s.match.phase = 'warmup';
    s.warmup = this.settings.warmupSeconds ?? WARMUP_SECONDS;
    for (const p of s.players) p.ready = false;
    out.push({ type: 'match-start' });
  }

  /**
   * Carries a set from one round to the next.
   *
   * The shape is the one every round-based shooter uses and is worth naming: a
   * round ends, the result sits on screen for a few seconds while the score
   * updates, and then the next round starts with everybody alive and the map put
   * back. Without the pause the scoreboard is a frame long and the only thing a
   * player learns about the round they just lost is that it is over.
   *
   * A room with no series does none of this — a single match ends, stays ended,
   * and is read at leisure.
   */
  private tickSeries(dt: number, out: SimEvent[]): void {
    const s = this.state;
    if (!s.series) return;
    /*
     * A finished set is finished.
     *
     * Without this the last round never ends: the set is decided, the scoreboard
     * runs down, the phase goes back to `over` — and the next tick reads `over`
     * as a round that has just been decided and scores it again, forever. The
     * room would have played an unbounded best-of-three, each round announced,
     * each one incrementing a score nothing was still reading.
     */
    if (this.seriesDone && s.match.phase === 'over') return;

    // A round has just been decided. Record it before anything else, because the
    // scoreboard about to go up is showing what this writes.
    if (s.match.phase === 'over') {
      endRound(s.series, s.match.winner);
      s.match.phase = 'intermission';
      s.intermission = INTERMISSION_SECONDS;

      const done = seriesOver(s.series);
      out.push({
        type: 'round-over',
        winner: s.match.winner,
        wins: { ...s.series.wins },
        played: s.series.played,
        bestOf: s.series.bestOf,
        seriesWinner: done ? seriesWinner(s.series) : null,
        nextMap: done ? null : currentMap(s.series),
      });
      /*
       * The set finishing leaves the *round* over rather than starting another.
       * `intermission` still runs, so the last scoreboard behaves like all the
       * others and nobody is dropped out of a match the instant it is decided.
       */
      if (done) this.seriesDone = true;
      return;
    }

    if (s.match.phase !== 'intermission') return;

    s.intermission -= dt;
    if (s.intermission > 0) return;
    s.intermission = 0;

    if (this.seriesDone) {
      // Back to a finished match, which is what everything upstream already
      // knows how to display.
      s.match.phase = 'over';
      return;
    }
    this.beginRound(out);
  }

  /**
   * Puts the arena and everybody in it back, and starts the next round.
   *
   * Health, power, position and effects belong to the round that has ended;
   * levels belong to the set, or not, depending on what the host chose. That
   * split is `carryInto`'s and is deliberately not decided again here.
   */
  private beginRound(out: SimEvent[]): void {
    const s = this.state;
    if (!s.series) return;

    const map = currentMap(s.series);
    const next = arenaById(map);
    /*
     * A fresh arena rather than a cleaned one.
     *
     * Shrines, pools and their ownership are all on the `Arena`, and rebuilding
     * it from the map's own maker is both shorter and safer than walking it
     * putting each field back — a reset that forgets one field leaves a round
     * starting with somebody already holding a pool, which is the kind of bug
     * that shows up as an argument between players rather than as an error.
     */
    this.settings.arena = next.make();
    this.settings.geometry = next.geometry;
    s.arena = this.settings.arena;

    /*
     * The colliders and the bots' map, not just the objectives.
     *
     * Three things describe a level and they are held in three places: the
     * `Arena` core reasons about, the physics colliders movement and line of
     * sight resolve against, and the nav grid bots path over. Swapping the first
     * and leaving the other two is the desync that looks like nothing is wrong —
     * the right shrines in the right places, players walking through walls that
     * are no longer drawn, and bots pathing confidently around a building that
     * is not there.
     */
    this.world.rebuildLevel?.(next.geometry);
    this.director.repath(buildNavGrid(next.geometry));

    carryInto(s.players, s.series.carryXp);
    /*
     * Counted per side as they are placed, so a round starts spread out.
     *
     * The freeze made this visible: everybody used to walk apart in the first
     * second and nobody saw that they had started inside each other. Ten seconds
     * of standing still with a teammate's head through your camera is a
     * different experience.
     */
    const placed: Record<Faction, number> = { order: 0, balance: 0, chaos: 0 };
    for (const p of s.players) {
      const spawn = this.spawnFor(p.faction, placed[p.faction]++);
      p.pos = { ...spawn };
      p.prevPos = { ...spawn };
      this.world.place?.(p);
    }

    s.projectiles = [];
    s.storms = [];
    s.barriers = [];
    s.signs = [];
    s.match = makeMatch(this.settings.roundSeconds);
    /*
     * A round starts frozen, not live.
     *
     * Everybody is standing on a spawn on a map that may not be the one they
     * were just on, and the ten seconds before the clock starts is when they
     * work out which. `makeMatch` already returns `warmup`; what it cannot know
     * is how long that should last.
     */
    s.warmup = this.settings.warmupSeconds ?? WARMUP_SECONDS;

    out.push({
      type: 'round-start',
      round: s.series.played + 1,
      bestOf: s.series.bestOf,
      mapId: map,
      wins: { ...s.series.wins },
    });
  }

  /** Makes every bot plan a route before the first tick, off the loading screen. */
  warmUpBots(): void {
    if (this.bots.size) this.director.inputs(this.state, [...this.bots], TICK_DT);
  }

  /**
   * When this room last had nobody in it, or null while somebody is here.
   *
   * A room is not reaped the instant it empties, and the reason is a race rather
   * than politeness. Opening a room and sitting down in it are two messages, and
   * between them the room has nobody in it — so a reaper that fires on the tick
   * deletes a room a fraction of a second before its host arrives, and the host
   * is told there is no such room. The same window opens whenever the last
   * player disconnects a moment before a friend's join lands.
   */
  private emptySince: number | null = null;

  /**
   * Marks the room as newly opened, so the grace window starts now.
   *
   * Called when a room is created rather than left to the first sweep, because
   * `emptySince` starting at null would mean "occupied" for a room nobody has
   * ever been in — which is the correct answer for a room being joined this
   * instant and the wrong one for a room whose host never came back.
   */
  holdOpen(now: number, seconds = ABANDON_GRACE): void {
    this.emptySince = now;
    this.heldUntil = now + seconds;
  }

  /** Until when this room may not be reaped, however empty it is. */
  private heldUntil = 0;

  /**
   * Nobody left, and long enough ago to be sure.
   *
   * The old version returned `this.state.players.every(...)`, which on an empty
   * array is `true` — so a room with no players in it at all reported itself
   * abandoned by vacuous truth rather than by anything anybody decided. That was
   * harmless only because nothing ever created a non-persistent room; it would
   * have deleted every room a host opened, one tick after opening it.
   *
   * Written out longhand now: a room with nobody in it is empty, a room holding
   * only bots is empty, and a room is reaped when it has been empty for a while
   * rather than for an instant.
   */
  abandoned(now: number, grace = ABANDON_GRACE): boolean {
    if (this.settings.persistent) return false;
    /*
     * A room opened for a lobby is held through the wait.
     *
     * The pre-match lobby is a *client* screen — the host sits in it choosing a
     * class and waiting for people, for up to two minutes — and the room has to
     * exist on the server that whole time or nobody can see it to join. Empty
     * for thirty seconds is the right rule for a room a match has drained; it is
     * the wrong one for a room that has not started yet.
     */
    if (now < this.heldUntil) return false;

    const occupied = this.state.players.some((p) => !this.bots.has(p.id));
    if (occupied) {
      this.emptySince = null;
      return false;
    }

    if (this.emptySince === null) this.emptySince = now;
    return now - this.emptySince >= grace;
  }
}

/** How far a dummy will shoot. Far enough to matter, near enough to escape. */
const DUMMY_RANGE = 40;

/** An input nobody sent, for a member whose client has gone quiet. */
export const IDLE: PlayerInput = emptyInput();
